// Hook React para consumir índices econômicos
// Fornece interface simples para componentes frontend
import { useState, useEffect, useCallback } from 'react';
import { 
  getLatestBySeries, 
  getRangeBySeries, 
  getLastNBySeries,
  getAllSeriesStatus,
  updateIndices,
  IndexPoint,
  ECONOMIC_SERIES,
  formatPercentage,
  formatDate,
  isSelicSeries
} from '@/services/indicesService';
import { formatPct } from '@/utils/formatters';
import { useToast } from '@/hooks/use-toast';

interface UseIndicesReturn {
  // Estado
  loading: boolean;
  error: string | null;
  lastUpdate: Date | null;
  
  // Dados
  getLatest: (seriesCode: number) => Promise<IndexPoint | null>;
  getRange: (seriesCode: number, fromDate: string, toDate: string) => Promise<IndexPoint[]>;
  getLastN: (seriesCode: number, count?: number) => Promise<IndexPoint[]>;
  getAllStatus: () => Promise<Record<number, IndexPoint | null>>;
  
  // Controles
  refresh: () => Promise<void>;
  forceUpdate: (seriesCodes: number[], mode?: 'daily' | 'monthly') => Promise<boolean>;
  
  // Utilitários
  formatValue: (value: number) => string;
  formatDateBR: (isoDate: string) => string;
  isSelic: (seriesCode: number) => boolean;
  getSeriesInfo: (seriesCode: number) => typeof ECONOMIC_SERIES[number] | null;
}

/**
 * Hook principal para gerenciar índices econômicos
 */
export function useIndices(): UseIndicesReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const { toast } = useToast();

  // Busca último valor de uma série
  const getLatest = useCallback(async (seriesCode: number): Promise<IndexPoint | null> => {
    try {
      setError(null);
      const result = await getLatestBySeries(seriesCode);
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar índice';
      setError(errorMsg);
      return null;
    }
  }, []);

  // Busca série em período específico
  const getRange = useCallback(async (
    seriesCode: number, 
    fromDate: string, 
    toDate: string
  ): Promise<IndexPoint[]> => {
    try {
      setError(null);
      const result = await getRangeBySeries(seriesCode, fromDate, toDate);
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar período';
      setError(errorMsg);
      return [];
    }
  }, []);

  // Busca últimos N valores
  const getLastN = useCallback(async (seriesCode: number, count: number = 12): Promise<IndexPoint[]> => {
    try {
      setError(null);
      const result = await getLastNBySeries(seriesCode, count);
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar histórico';
      setError(errorMsg);
      return [];
    }
  }, []);

  // Busca status de todas as séries
  const getAllStatus = useCallback(async (): Promise<Record<number, IndexPoint | null>> => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAllSeriesStatus();
      setLastUpdate(new Date());
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar status';
      setError(errorMsg);
      return {};
    } finally {
      setLoading(false);
    }
  }, []);

  // Força refresh dos dados
  const refresh = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await getAllStatus();
      toast({
        title: "Índices atualizados",
        description: "Dados recarregados com sucesso"
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao atualizar';
      setError(errorMsg);
      toast({
        variant: "destructive",
        title: "Erro ao atualizar",
        description: errorMsg
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Força atualização via Edge Function
  const forceUpdate = useCallback(async (
    seriesCodes: number[], 
    mode: 'daily' | 'monthly' | 'manual' = 'manual'
  ): Promise<boolean> => {
    console.log(`🚀 [DEBUG] forceUpdate chamado:`, { seriesCodes, mode });
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🚀 [DEBUG] Chamando updateIndices...`);
      const result = await updateIndices(seriesCodes, mode);
      console.log(`🚀 [DEBUG] Resultado completo updateIndices:`, result);
      
      if (result?.ok) {
        setLastUpdate(new Date());
        const processedCount = Object.keys(result.data || {}).length;
        console.log(`✅ [DEBUG] Sucesso - ${processedCount} séries atualizadas`);
        console.log(`✅ [DEBUG] Dados atualizados:`, result.data);
        
        toast({
          title: "Atualização concluída",
          description: `${processedCount} séries atualizadas com sucesso`
        });
        return true;
      } else {
        console.error(`❌ [DEBUG] Resultado não OK:`, result);
        throw new Error(result?.error || 'Erro na atualização');
      }
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao forçar atualização';
      console.error(`❌ [DEBUG] Erro em forceUpdate:`, err);
      setError(errorMsg);
      toast({
        variant: "destructive",
        title: "Erro na atualização",
        description: errorMsg
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Utilitário para formatar valor como percentual
  const formatValue = useCallback((value: number): string => {
    return formatPct(value);
  }, []);

  // Utilitário para formatar data
  const formatDateBR = useCallback((isoDate: string): string => {
    return formatDate(isoDate);
  }, []);

  // Utilitário para verificar se é SELIC
  const isSelic = useCallback((seriesCode: number): boolean => {
    return isSelicSeries(seriesCode);
  }, []);

  // Utilitário para obter informações da série
  const getSeriesInfo = useCallback((seriesCode: number) => {
    return ECONOMIC_SERIES[seriesCode] || null;
  }, []);

  return {
    // Estado
    loading,
    error,
    lastUpdate,
    
    // Dados
    getLatest,
    getRange,
    getLastN,
    getAllStatus,
    
    // Controles  
    refresh,
    forceUpdate,
    
    // Utilitários
    formatValue,
    formatDateBR,
    isSelic,
    getSeriesInfo
  };
}

/**
 * Hook específico para página de cálculos
 * Retorna dados formatados para exibição
 */
export function useCalculationIndices() {
  const { getLatest, formatValue, formatDateBR, isSelic, loading } = useIndices();
  const [indicesData, setIndicesData] = useState<Record<string, { label: string; isUpdated: boolean }>>({});

  // Carrega dados dos índices principais para cálculos
  const loadIndicesForCalculation = useCallback(async () => {
    const updates: Record<string, { label: string; isUpdated: boolean }> = {};
    
    // IPCA
    const ipca = await getLatest(433);
    updates.IPCA = {
      label: ipca ? `IPCA (${formatDateBR(ipca.date)}) - ${formatValue(ipca.value)}` : 'IPCA - Carregando...',
      isUpdated: !!ipca
    };
    
    // INPC
    const inpc = await getLatest(188);
    updates.INPC = {
      label: inpc ? `INPC (${formatDateBR(inpc.date)}) - ${formatValue(inpc.value)}` : 'INPC - Carregando...',
      isUpdated: !!inpc
    };
    
    // TR
    const tr = await getLatest(226);
    updates.TR = {
      label: tr ? `TR (${formatDateBR(tr.date)}) - ${formatValue(tr.value)}` : 'TR - Carregando...',
      isUpdated: !!tr
    };
    
    // SELIC
    const selic = await getLatest(4390);
    updates.SELIC = {
      label: selic ? `SELIC (${formatDateBR(selic.date)}) - ${formatValue(selic.value)}` : 'SELIC - Carregando...',
      isUpdated: !!selic
    };
    
    setIndicesData(updates);
  }, [getLatest, formatValue, formatDateBR]);

  useEffect(() => {
    loadIndicesForCalculation();
  }, [loadIndicesForCalculation]);

  return {
    indicesData,
    loading,
    refresh: loadIndicesForCalculation,
    isSelic
  };
}